import { usePerformance } from '../hooks/useWpApi';
import { SilviaLoader } from '../components/SilviaLoader';
import { RefreshCw, AlertTriangle, Heart, Zap, Globe, ShoppingCart, BarChart3, Clock } from 'lucide-react';
import '../styles/Performance.css';

export function Performance() {
  const { data, loading, error, refresh, lastRefresh } = usePerformance(120_000);

  if (loading && !data) {
    return <SilviaLoader text="Ładowanie wydajności..." />;
  }

  if (error && !data) {
    return (
      <div className="stats-error">
        <AlertTriangle size={32} />
        <p>{error}</p>
        <button className="btn-refresh" onClick={refresh}>Spróbuj ponownie</button>
      </div>
    );
  }

  if (!data) return null;

  const { rum, health, page_speed, ajax_speed, checkout } = data;

  return (
    <div className="stats-page">
      <div className="stats-header">
        <div className="stats-header-top">
          <h1>⚡ Wydajność</h1>
          <button className="btn-icon-refresh" onClick={refresh} title="Odśwież">
            <RefreshCw size={18} className={loading ? 'spinning' : ''} />
          </button>
        </div>
        {lastRefresh && (
          <span className="stats-updated">Aktualizacja: {lastRefresh.toLocaleTimeString('pl-PL')}</span>
        )}
      </div>

      {/* ── Health Score ── */}
      <section className="stats-section">
        <h2 className="section-title"><Heart size={18} />System Health</h2>
        <div className="perf-health-hero">
          <div className={`perf-score-ring ${health.score > 80 ? 'good' : health.score > 60 ? 'warn' : 'bad'}`}>
            <svg viewBox="0 0 100 100" className="score-svg">
              <circle cx="50" cy="50" r="42" className="score-bg" />
              <circle cx="50" cy="50" r="42" className="score-fg"
                strokeDasharray={`${(health.score / 100) * 264} 264`} />
            </svg>
            <div className="score-text">
              <span className="score-number">{health.score}</span>
              <span className="score-pct">%</span>
            </div>
          </div>
          <span className="perf-health-status">
            {health.status === 'healthy' ? '✅ System zdrowy' : health.status === 'degraded' ? '⚠️ Problemy wykryte' : '❌ Stan krytyczny'}
          </span>
        </div>

        {health.checks && Object.keys(health.checks).length > 0 && (
          <div className="health-detail-list">
            {Object.entries(health.checks).map(([key, check]) => {
              const c = check as { status?: string; label?: string; value?: string | number | null; details?: Record<string, unknown> };
              return (
                <div key={key} className={`health-detail-item status-${c.status || 'unknown'}`}>
                  <span className="hd-icon">
                    {c.status === 'ok' ? '✅' : c.status === 'warning' ? '⚠️' : c.status === 'critical' ? '❌' : '❓'}
                  </span>
                  <span className="hd-name">{c.label || key}</span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── RUM Metrics ── */}
      {rum && rum.total_records > 0 && (
        <section className="stats-section">
          <h2 className="section-title"><Zap size={18} />Real User Metrics (24h)</h2>
          <div className="rum-visitors-bar">
            <span>👥 {rum.visitors} unikanych użytkowników</span>
            <span>📊 {rum.total_records} pomiarów</span>
          </div>

          <div className="rum-metrics-list">
            {Object.entries(rum.averages).map(([type, metric]) => {
              const thresholds = getRumThresholds(type);
              const status = metric.avg <= thresholds.good ? 'good' : metric.avg <= thresholds.ok ? 'ok' : 'bad';
              return (
                <div key={type} className={`rum-metric-card status-${status}`}>
                  <div className="rum-metric-header">
                    <span className="rum-metric-name">{rumLabel(type)}</span>
                    <span className="rum-metric-badge">{metric.cnt}</span>
                  </div>
                  <div className="rum-metric-value">{formatMs(metric.avg)}</div>
                  <div className="rum-metric-range">
                    <span>min {formatMs(metric.min)}</span>
                    <span>max {formatMs(metric.max)}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Hourly chart */}
          {rum.hourly && rum.hourly.length > 0 && (
            <div className="rum-hourly-section">
              <h3 className="subsection-title"><BarChart3 size={16} />Trend godzinowy</h3>
              <div className="hourly-chart">
                {aggregateHourly(rum.hourly).map((h, i) => {
                  const maxVal = Math.max(...aggregateHourly(rum.hourly).map(x => x.avg), 1);
                  const barH = Math.max((h.avg / maxVal) * 100, 5);
                  return (
                    <div key={i} className="hourly-bar-col">
                      <div className="hourly-bar-wrapper">
                        <div className={`hourly-bar ${h.avg < 2000 ? 'good' : h.avg < 4000 ? 'ok' : 'bad'}`}
                          style={{ height: `${barH}%` }} />
                      </div>
                      <span className="hourly-label">{h.hour.replace(':00', '')}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      )}

      {/* ── Page Speed ── */}
      {page_speed && (
        <section className="stats-section">
          <h2 className="section-title"><Globe size={18} />Page Speed (Bot)</h2>
          {page_speed.avg_fcp_ms !== null && (
            <div className={`perf-avg-card ${page_speed.avg_fcp_ms < 2000 ? 'good' : page_speed.avg_fcp_ms < 4000 ? 'ok' : 'bad'}`}>
              <span className="perf-avg-label">Średni FCP</span>
              <span className="perf-avg-value">{formatMs(page_speed.avg_fcp_ms)}</span>
            </div>
          )}
          {page_speed.pages && page_speed.pages.length > 0 && (
            <div className="page-list">
              {page_speed.pages.map((page, i) => (
                <div key={i} className="page-item">
                  <span className="page-name">{page.name || page.url || `Strona ${i + 1}`}</span>
                  <div className="page-metrics">
                    {page.fcp_ms !== undefined && (
                      <span className={`page-metric ${page.fcp_ms < 2000 ? 'good' : page.fcp_ms < 4000 ? 'ok' : 'bad'}`}>
                        FCP {formatMs(page.fcp_ms)}
                      </span>
                    )}
                    {page.lcp_ms !== undefined && (
                      <span className={`page-metric ${page.lcp_ms < 2500 ? 'good' : page.lcp_ms < 4000 ? 'ok' : 'bad'}`}>
                        LCP {formatMs(page.lcp_ms)}
                      </span>
                    )}
                    {page.ttfb_ms !== undefined && (
                      <span className={`page-metric ${page.ttfb_ms < 800 ? 'good' : page.ttfb_ms < 1800 ? 'ok' : 'bad'}`}>
                        TTFB {formatMs(page.ttfb_ms)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {page_speed.tested_at && (
            <span className="perf-tested-at"><Clock size={12} /> {page_speed.tested_at}</span>
          )}
        </section>
      )}

      {/* ── AJAX Speed ── */}
      {ajax_speed && (
        <section className="stats-section">
          <h2 className="section-title"><Zap size={18} />AJAX Speed (Bot)</h2>
          {ajax_speed.avg_ajax_ms !== null && (
            <div className={`perf-avg-card ${ajax_speed.avg_ajax_ms < 1500 ? 'good' : ajax_speed.avg_ajax_ms < 3000 ? 'ok' : 'bad'}`}>
              <span className="perf-avg-label">Średni AJAX</span>
              <span className="perf-avg-value">{formatMs(ajax_speed.avg_ajax_ms)}</span>
            </div>
          )}
          {ajax_speed.actions && ajax_speed.actions.length > 0 && (
            <div className="page-list">
              {ajax_speed.actions.map((action, i) => (
                <div key={i} className="page-item">
                  <span className="page-name">{action.name || action.action || `Akcja ${i + 1}`}</span>
                  {action.duration_ms !== undefined && (
                    <span className={`page-metric ${action.duration_ms < 1500 ? 'good' : action.duration_ms < 3000 ? 'ok' : 'bad'}`}>
                      {formatMs(action.duration_ms)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
          {ajax_speed.tested_at && (
            <span className="perf-tested-at"><Clock size={12} /> {ajax_speed.tested_at}</span>
          )}
        </section>
      )}

      {/* ── Checkout E2E ── */}
      {checkout && (
        <section className="stats-section">
          <h2 className="section-title"><ShoppingCart size={18} />Checkout E2E</h2>
          <div className={`checkout-hero ${checkout.success ? 'success' : 'fail'}`}>
            <span className="checkout-status">{checkout.success ? '✅ OK' : '❌ Błąd'}</span>
            {checkout.total_duration_ms !== null && (
              <span className="checkout-total">{formatMs(checkout.total_duration_ms)}</span>
            )}
          </div>
          {checkout.steps && checkout.steps.length > 0 && (
            <div className="checkout-steps">
              {checkout.steps.map((step, i) => (
                <div key={i} className={`checkout-step status-${step.status}`}>
                  <span className="step-num">{i + 1}</span>
                  <span className="step-name">{step.name}</span>
                  {step.duration_ms !== null && (
                    <span className="step-duration">{formatMs(step.duration_ms)}</span>
                  )}
                  <span className="step-icon">{step.status === 'ok' || step.status === 'passed' ? '✅' : step.status === 'skipped' ? '⏭️' : '❌'}</span>
                </div>
              ))}
            </div>
          )}
          {checkout.tested_at && (
            <span className="perf-tested-at"><Clock size={12} /> {checkout.tested_at}</span>
          )}
        </section>
      )}
    </div>
  );
}

// ── Helpers ──

function formatMs(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function rumLabel(type: string): string {
  const labels: Record<string, string> = {
    page_load: 'Page Load', fcp: 'First Contentful Paint', lcp: 'Largest Contentful Paint',
    cls: 'Cumulative Layout Shift', fid: 'First Input Delay', inp: 'Interaction to Next Paint',
    ttfb: 'Time to First Byte', dom_interactive: 'DOM Interactive', dom_complete: 'DOM Complete',
  };
  return labels[type] || type;
}

function getRumThresholds(type: string): { good: number; ok: number } {
  const t: Record<string, { good: number; ok: number }> = {
    page_load: { good: 3000, ok: 5000 }, fcp: { good: 1800, ok: 3000 },
    lcp: { good: 2500, ok: 4000 }, fid: { good: 100, ok: 300 },
    inp: { good: 200, ok: 500 }, ttfb: { good: 800, ok: 1800 },
    cls: { good: 100, ok: 250 }, // scaled ×1000
    dom_interactive: { good: 2000, ok: 4000 }, dom_complete: { good: 3000, ok: 5000 },
  };
  return t[type] || { good: 2000, ok: 4000 };
}

function aggregateHourly(hourly: Array<{ hour: string; avg: number; cnt: number }>): Array<{ hour: string; avg: number }> {
  const map = new Map<string, { total: number; cnt: number }>();
  hourly.forEach(h => {
    const prev = map.get(h.hour) || { total: 0, cnt: 0 };
    map.set(h.hour, { total: prev.total + h.avg * h.cnt, cnt: prev.cnt + h.cnt });
  });
  return Array.from(map.entries()).map(([hour, v]) => ({
    hour,
    avg: Math.round(v.total / v.cnt),
  }));
}
